import classNames from "classnames";
import Button from "kustom-front/src/components/atoms/Button";
import React, { FunctionComponent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import CartButtonSpinner from "@Components/atoms/cart/CartButtonSpinner";
import CartInput from "@Components/atoms/cart/CartInput";
import CartAddressContainer from "@Components/molecules/cart/CartAddressContainer";
import CartAddressFormBilling from "@Components/molecules/cart/CartAddressFormBilling";
import CartCountrySelect from "@Components/molecules/cart/CartCountrySelect";
import CheckboxWithLabel from "@Components/molecules/cart/CheckboxWithLabel";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { countries as countriesList } from "@Samples/mocks/cart";
import { Addresses, BillingAddress, DeliveryAddress } from "@Types/addresses";
import { AutoCompletePlace } from "@Types/addresses";
import { Country } from "@Types/cart";
import UserContent from "@Types/user";
import { emailPattern, userNamePattern } from "@Utils/checkFormats";
import CartAutoCompleteStreetInput from "./CartAutoCompleteStreetInput";
import "./style.scss";

interface Props extends ContainerProps {
  address: Addresses;
  user: UserContent;
}

interface ContainerProps {
  countries: Country[];
  disabled?: boolean;
  onSubmit: ({
    billing,
    delivery,
  }: {
    billing: Partial<BillingAddress> | null;
    delivery: Partial<DeliveryAddress>;
  }) => void;
}

type FormEvent = React.FormEvent<HTMLFormElement> | React.MouseEvent<Element, MouseEvent>;

export const CartAddressForm: FunctionComponent<Props> = ({
  address: { delivery, billing },
  countries = [],
  disabled = false,
  onSubmit,
  user,
}) => {
  const intl = useIntl();
  const [city, setCity] = useState<string | undefined>(delivery.city || "");
  const [company, setCompany] = useState<string | undefined>(delivery.company || "");
  const [country, setCountry] = useState<string | undefined>(delivery.country);
  const [countryTag, setCountryTag] = useState<string | undefined>(delivery.countryTag);
  const [countryTag2, setCountryTag2] = useState<string | undefined>(delivery.countryTag2);
  const [details, setDetails] = useState<string | undefined>(delivery.details || "");
  const [email, setEmail] = useState<string | undefined>(user.email || "");
  const [emailConfirmation, setEmailConfirmation] = useState<string | undefined>("");
  const [firstName, setFirstName] = useState<string | undefined>(delivery.firstName || "");
  const [lastName, setLastName] = useState<string | undefined>(delivery.lastName || "");
  const [phone, setPhone] = useState<string | undefined>(delivery.phone || "");
  const [postcode, setPostcode] = useState<string | undefined>(delivery.postcode || "");
  const [street, setStreet] = useState<string | undefined>(delivery.street || "");

  const [billingCity, setBillingCity] = useState<string | undefined>(billing.city || "");
  const [billingCompany, setBillingCompany] = useState<string | undefined>(billing.company || "");
  const [billingCountry, setBillingCountry] = useState<string | undefined>(billing.country);
  const [billingCountryTag, setBillingCountryTag] = useState<string | undefined>(
    billing.countryTag
  );
  const [billingCountryTag2, setBillingCountryTag2] = useState<string | undefined>(
    billing.countryTag2
  );
  const [billingDetails, setBillingDetails] = useState<string | undefined>(billing.details || "");
  const [billingFirstName, setBillingFirstName] = useState<string | undefined>(
    billing.firstName || ""
  );
  const [billingLastName, setBillingLastName] = useState<string | undefined>(
    billing.lastName || ""
  );
  const [billingPostcode, setBillingPostcode] = useState<string | undefined>(
    billing.postcode || ""
  );
  const [billingStreet, setBillingStreet] = useState<string | undefined>(billing.street || "");
  const [billingTvaNumber, setBillingTvaNumber] = useState<string | undefined>(
    billing.tvaNumber || ""
  );

  const [sameAddress, setSameAddress] = useState<boolean>(!billing.firstName);
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);

  const { dispatchNotification } = useNotificationsContext();

  const handleSameAddress = useCallback(() => {
    setSameAddress(!sameAddress);
    setSubmittedForm(false);
  }, [setSameAddress, sameAddress]);

  const address = {
    city,
    company,
    country,
    countryTag,
    countryTag2,
    details,
    firstName,
    lastName,
    postcode,
    street,
  };

  const billingAddress = {
    city: billingCity,
    company: billingCompany,
    country: billingCountry,
    countryTag: billingCountryTag,
    countryTag2: billingCountryTag2,
    details: billingDetails,
    firstName: billingFirstName,
    lastName: billingLastName,
    postcode: billingPostcode,
    street: billingStreet,
    tvaNumber: billingTvaNumber,
  };

  const addressValues = {
    billing: !sameAddress ? billingAddress : null,
    delivery: { ...address, email, emailConfirmation, phone },
  };

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      setSubmittedForm(true);
      if (
        !addressValues.delivery.city ||
        !addressValues.delivery.country ||
        !addressValues.delivery.email ||
        !addressValues.delivery.firstName ||
        !addressValues.delivery.lastName ||
        !addressValues.delivery.phone ||
        !addressValues.delivery.postcode ||
        !addressValues.delivery.street
      ) {
        dispatchNotification({
          code: "CFE-002",
          type: "addNotificationByCode",
        });
        return;
      }
      if (
        user.isGuest &&
        addressValues.delivery.email !== addressValues.delivery.emailConfirmation
      ) {
        dispatchNotification({
          code: "CFE-003",
          type: "addNotificationByCode",
        });
        return;
      }
      if (
        !sameAddress &&
        (!addressValues.billing ||
          !addressValues.billing.city ||
          !addressValues.billing.firstName ||
          !addressValues.billing.lastName ||
          !addressValues.billing.postcode ||
          !addressValues.billing.street)
      ) {
        dispatchNotification({
          code: "CFE-002",
          type: "addNotificationByCode",
        });
        return;
      }
      onSubmit(addressValues);
    },
    [addressValues, dispatchNotification, onSubmit, sameAddress]
  );

  const handleChangeBilling = useCallback((field: string, value: string) => {
    switch (field) {
      case "firstName":
        setBillingFirstName(value);
        break;
      case "lastName":
        setBillingLastName(value);
        break;
      case "city":
        setBillingCity(value);
        break;
      case "company":
        setBillingCompany(value);
        break;
      case "country":
        setBillingCountry(value);
        break;
      case "countryTag":
        setBillingCountryTag(value);
        break;
      case "countryTag2":
        setBillingCountryTag2(value);
        break;
      case "postcode":
        setBillingPostcode(value);
        break;
      case "details":
        setBillingDetails(value);
        break;
      case "street":
        setBillingStreet(value);
        break;
      case "tvaNumber":
        setBillingTvaNumber(value);
        break;
      default:
        return;
    }
  }, []);

  const handleSelectStreet = useCallback(
    (place: AutoCompletePlace) => {
      const autoCompleteStreet =
        place.street_number !== ""
          ? `${place.street_number || ""} ${place.route || ""}`
          : `${place.route || ""}`;

      const selectedCountry = countriesList.find((value) => value.tag === place.country);

      if (selectedCountry === undefined) {
        dispatchNotification({
          code: "CFE-005",
          type: "addNotificationByCode",
        });
        return;
      }

      setCity(place.locality);
      setCountry(selectedCountry && selectedCountry.name);
      setCountryTag(selectedCountry && selectedCountry.tag);
      setCountryTag2(selectedCountry && selectedCountry.tagAlpha3);
      setPostcode(place.postal_code);
      setStreet(autoCompleteStreet);
    },
    [setCity, setCountry, setPostcode, setStreet]
  );

  const handleChangeStreet = useCallback((value: string) => {
    setStreet(value);
  }, []);

  const handleChangeCountry = useCallback(
    (selectedCountry: Country) => {
      setCountry(selectedCountry.name);
      setCountryTag(selectedCountry.tag);
      setCountryTag2(selectedCountry.tagAlpha3);
    },
    [setCountry, setCountryTag, setCountryTag2]
  );

  let privacyLink: string;
  switch (intl.locale) {
    case "de":
      privacyLink = "https://www.iubenda.com/privacy-policy/833692/full-legal";
      break;
    case "es":
      privacyLink = "https://www.iubenda.com/privacy-policy/8084956/full-legal";
      break;
    case "fr":
      privacyLink = "https://www.iubenda.com/privacy-policy/833692/full-legal";
      break;
    case "it":
      privacyLink = "https://www.iubenda.com/privacy-policy/567132/full-legal";
      break;
    default:
      privacyLink = "https://www.iubenda.com/privacy-policy/567132/full-legal";
  }

  return (
    <form
      className={classNames("CartAddressForm", {
        submitted: submittedForm,
      })}
      onSubmit={handleSubmit}
    >
      <CartAddressContainer type="delivery">
        <div className="CartAddressForm-fieldLine">
          <div className="CartAddressForm-inputContainer">
            <CartInput
              autocomplete="shipping given-name"
              name="firstName"
              required={true}
              value={firstName}
              placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_firstName" })}
              pattern={userNamePattern}
              onChange={setFirstName}
            />
          </div>
          <div className="CartAddressForm-inputContainer">
            <CartInput
              autocomplete="shipping family-name"
              name="lastName"
              value={lastName}
              placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_lastName" })}
              required={true}
              pattern={userNamePattern}
              onChange={setLastName}
            />
          </div>
          <div className="CartAddressForm-inputContainer">
            <CartInput
              autocomplete="shipping tel"
              name="phone"
              value={phone}
              placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_phone" })}
              required={true}
              type="tel"
              onChange={setPhone}
            />
          </div>
        </div>
        {user.isGuest && (
          <>
            <div className="CartAddressForm-fieldLine">
              <CartInput
                autocomplete="shipping email"
                name="email"
                value={email}
                placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_email" })}
                required={true}
                type="email"
                pattern={emailPattern}
                onChange={setEmail}
              />
            </div>
            <div className="CartAddressForm-fieldLine">
              <CartInput
                autocomplete="off"
                name="emailConfirmation"
                value={emailConfirmation}
                placeholder={intl.formatMessage({
                  id: "cheerz_CartAddressForm_email_confirmation",
                })}
                required={true}
                type="email"
                pattern={emailPattern}
                onChange={setEmailConfirmation}
                onPaste={false}
              />
            </div>
          </>
        )}
        <div className="CartAddressForm-fieldLine">
          <CartInput
            autocomplete="shipping organization"
            name="company"
            value={company}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_company" })}
            onChange={setCompany}
          />
        </div>
        <div className="CartAddressForm-fieldLine">
          <CartAutoCompleteStreetInput
            onChange={handleChangeStreet}
            onSelect={handleSelectStreet}
            value={street}
          />
        </div>
        <div className="CartAddressForm-fieldLine">
          <CartInput
            autocomplete="shipping address-line1"
            name="additionalDetails"
            value={details}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_details" })}
            onChange={setDetails}
          />
        </div>
        <div className="CartAddressForm-fieldLine">
          <div className="CartAddressForm-inputContainer">
            <CartInput
              autocomplete="shipping postal-code"
              name="postcode"
              value={postcode}
              placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_postcode" })}
              required={true}
              onChange={setPostcode}
            />
          </div>
          <div className="CartAddressForm-inputContainer">
            <CartInput
              name="city"
              value={city}
              placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_city" })}
              required={true}
              onChange={setCity}
            />
          </div>
          <div className="CartAddressForm-countrySelectContainer">
            <CartCountrySelect
              countries={countries}
              onSelect={handleChangeCountry}
              selectedCountry={country}
            />
          </div>
        </div>
        <div className="CartAddressForm-checkboxContainer">
          <CheckboxWithLabel checked={sameAddress} onChange={handleSameAddress}>
            {intl.formatMessage({ id: "cheerz_CartAddressForm_same_address" })}
          </CheckboxWithLabel>
        </div>
      </CartAddressContainer>
      {!sameAddress && (
        <CartAddressFormBilling
          city={billingCity}
          company={billingCompany}
          country={billingCountry}
          details={billingDetails}
          firstName={billingFirstName}
          lastName={billingLastName}
          postcode={billingPostcode}
          street={billingStreet}
          tvaNumber={billingTvaNumber}
          countries={countries}
          onChange={handleChangeBilling}
        />
      )}
      <div className="CartAddressForm-linkContainer">
        <a
          className="CartAddressForm-linkContainer-link"
          href={privacyLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {intl.formatMessage({ id: "cheerz_CartAddressForm_privacy" })}
        </a>
      </div>

      <div className="CartAddressForm-buttonContainer">
        <Button type="accent" onClick={handleSubmit} disabled={disabled}>
          {disabled ? (
            <CartButtonSpinner />
          ) : (
            intl.formatMessage({ id: "cheerz_CartAddressForm_order" })
          )}
        </Button>
      </div>
    </form>
  );
};

const CartAddressFormContainer: FunctionComponent<ContainerProps> = ({
  countries,
  disabled,
  onSubmit,
}) => {
  const { addresses, user } = useCartContext();

  return (
    <CartAddressForm
      address={addresses}
      countries={countries}
      disabled={disabled}
      onSubmit={onSubmit}
      user={user}
    />
  );
};

export default CartAddressFormContainer;
